import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { setSession } from '../../../services/localStorage';

const LoginForm = ({ onToggle2FA, show2FA }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberDevice: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  // Mock credentials for testing
  const mockCredentials = {
    admin: { email: 'admin@vinpro.mx', password: 'Vinpro123.', userName: 'Admin User', role: 'admin' },
    operator: { email: 'operador@vinpro.mx', password: 'Operador123!', userName: 'Operador', role: 'operator' },
    auditor: { email: 'auditor@vinpro.mx', password: 'Auditor456!', userName: 'Auditor', role: 'auditor' }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear errors when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Formato de correo electrónico inválido';
    }

    if (!formData?.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleLogin = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Check mock credentials and get user data
      const matchedUser = Object.values(mockCredentials)?.find(
        cred => cred?.email === formData?.email && cred?.password === formData?.password
      );

      if (!matchedUser) {
        setErrors({
          general: `Credenciales inválidas. Use: ${Object.values(mockCredentials)?.map(c => c?.email)?.join(', ')}`
        });
        setIsLoading(false);
        return;
      }

      // Store session data with complete user info
      setSession({
        email: matchedUser.email,
        userName: matchedUser.userName,
        role: matchedUser.role,
        loginTime: new Date().toISOString(),
        rememberDevice: formData?.rememberDevice
      });

      // Navigate to dashboard
      navigate('/dashboard-overview');
    } catch (error) {
      setErrors({
        general: 'Error de conexión. Intente nuevamente.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e?.preventDefault();

    if (!resetEmail || !validateEmail(resetEmail)) {
      setErrors({ reset: 'Ingrese un correo electrónico válido' });
      return;
    }

    setIsLoading(true);

    try {
      // Simulate password reset
      await new Promise(resolve => setTimeout(resolve, 1000));

      setErrors({});
      alert('Se ha enviado un enlace de recuperación a su correo electrónico');
      setShowPasswordReset(false);
      setResetEmail('');
    } catch (error) {
      setErrors({ reset: 'Error al enviar el correo de recuperación' });
    } finally {
      setIsLoading(false);
    }
  };

  if (showPasswordReset) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-card border border-border rounded-lg shadow-elevation p-8">
          <div className="text-center mb-6">
            <Icon name="KeyRound" size={48} className="text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-foreground">Recuperar Contraseña</h2>
            <p className="text-muted-foreground mt-2">
              Ingrese su correo electrónico para recibir instrucciones de recuperación
            </p>
          </div>

          <form onSubmit={handlePasswordReset} className="space-y-6">
            <Input
              label="Correo Electrónico"
              type="email"
              placeholder="correo@ejemplo.com"
              value={resetEmail}
              onChange={(e) => setResetEmail(e?.target?.value)}
              error={errors?.reset}
              required
            />

            <div className="space-y-4">
              <Button
                type="submit"
                variant="default"
                fullWidth
                loading={isLoading}
                iconName="Send"
                iconPosition="left"
              >
                Enviar Enlace de Recuperación
              </Button>

              <Button
                type="button"
                variant="ghost"
                fullWidth
                onClick={() => setShowPasswordReset(false)}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Volver al Inicio de Sesión
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card border border-border rounded-lg shadow-elevation p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-lg mx-auto mb-4">
            <Icon name="Car" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground">VIN Pro Platform</h1>
          <p className="text-muted-foreground mt-2">
            Acceso seguro para profesionales automotrices
          </p>
        </div>

        {errors?.general && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 mb-6">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <p className="text-sm text-destructive">{errors?.general}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-6">
          <Input
            label="Correo Electrónico"
            type="email"
            name="email"
            placeholder="correo@empresa.com"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            required
          />

          <Input
            label="Contraseña"
            type="password"
            name="password"
            placeholder="Ingrese su contraseña"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
          />

          <div className="flex items-center justify-between">
            <Checkbox
              label="Recordar dispositivo"
              name="rememberDevice"
              checked={formData?.rememberDevice}
              onChange={handleInputChange}
            />

            <button
              type="button"
              onClick={() => setShowPasswordReset(true)}
              className="text-sm text-primary hover:text-primary/80 transition-smooth"
            >
              ¿Olvidó su contraseña?
            </button>
          </div>

          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={isLoading}
            iconName="LogIn"
            iconPosition="left"
          >
            Iniciar Sesión
          </Button>

          {!show2FA && (
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onToggle2FA}
              iconName="Shield"
              iconPosition="left"
            >
              Configurar Autenticación 2FA
            </Button>
          )}
        </form>
      </div>
    </div>
  );
};

export default LoginForm;